import React, { useState } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { submitPrescription } from '../../../Services/APIs/reimbursement';
import FormShell, { FormDone, focusFirstError, PRIMARY_BUTTON, SECONDARY_BUTTON, SECTION_HEADING } from './FormShell';
import { UploadField, useUploads } from './UploadField';
import { TextField, SelectField, ReviewField, ReviewFile } from './formFields';
import { PRESCRIPTION_FIELD_ORDER, validatePrescription, prescriptionPayload, wizardSteps, fieldDomId } from './formHelpers';
import { getPhonepeMembers, memberByUid, memberLabel } from '../phonepeSession';

const FORM_ID = 'prescription';
const STEPS = ['Details', 'Review'];

const PHOTOS = [
    { slot: 'upper_occlusal_photo', label: 'Upper Occlusal photo', required: true },
    { slot: 'lower_occlusal_photo', label: 'Lower Occlusal photo', required: true },
    { slot: 'problem_area_photo', label: 'Problem Area photo', required: true },
    { slot: 'additional_photo', label: 'Additional photo (optional)' },
    { slot: 'pre_op_xray_1', label: 'Pre-Op X-ray 1 (optional)' },
    { slot: 'pre_op_xray_2', label: 'Pre-Op X-ray 2 (optional)' },
];

const emptyFields = () => ({
    clinic_name: '',
    clinic_address: '',
    clinic_doctor: '',
    clinic_contact: '',
    clinical_notes: '',
});

// Starts a Reimbursement: the Reimbursement Prescription (the dentist's letterhead prescription
// plus clinical photos) is reviewed before the member gets treatment. Native 2-page replica of the
// dental360.toothlens.com/prescription form (ADR-0002). Each file uploads to S3 as soon as it's
// picked (see useUploads) — the form only ever submits the resulting URLs.
// Re-uploads a reviewer asks for later go through ReuploadForm, not this form.
// The patient is picked from the people on the member's policy and sent as their wallet uid.
const PrescriptionForm = ({ onClose, onSuccess }) => {
    const [step, setStep] = useState(1);
    const [members] = useState(getPhonepeMembers);
    // With only one person on the policy there is nothing to choose.
    const [patientUid, setPatientUid] = useState(() => (members.length === 1 ? members[0].uid : ''));
    const [fields, setFields] = useState(emptyFields);
    const [errors, setErrors] = useState({});
    const [confirmed, setConfirmed] = useState(false);
    const [confirmError, setConfirmError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [isDone, setIsDone] = useState(false);
    const { uploads, bind } = useUploads({ type: 'prescription' });

    const id = (name) => fieldDomId(FORM_ID, name);
    const clearError = (name) => setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    const text = (name) => ({
        id: id(name),
        name,
        value: fields[name],
        error: errors[name],
        onChange: (e) => {
            const { value } = e.target;
            setFields((prev) => ({ ...prev, [name]: value }));
            clearError(name);
        },
    });
    const upload = (slot) => {
        const props = bind(slot);
        return { ...props, onPick: (file) => { clearError(slot); props.onPick(file); } };
    };

    const patient = memberByUid(members, patientUid);

    const goToReview = (event) => {
        event.preventDefault();
        const found = validatePrescription({ members, patientUid, fields, uploads });
        setErrors(found);
        if (Object.keys(found).length) {
            focusFirstError(FORM_ID, found, PRESCRIPTION_FIELD_ORDER);
            return;
        }
        setFormError('');
        setStep(2);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!confirmed) {
            setConfirmError('Please confirm the information is accurate.');
            focusFirstError(FORM_ID, { confirm: true }, ['confirm']);
            return;
        }
        setIsSubmitting(true);
        setFormError('');
        try {
            await submitPrescription(prescriptionPayload({ fields, patientUid: patient.uid, uploads }));
            setIsDone(true);
        } catch (err) {
            setFormError(err?.response?.data?.message || 'Failed to submit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isDone) {
        return (
            <FormDone
                testId="prescription-submitted"
                title="Reimbursement requested"
                body="We'll review your reimbursement prescription and update its status here."
                onDone={onSuccess}
            />
        );
    }

    const footer = step === 1 ? (
        <button type="submit" form={FORM_ID} className={`${PRIMARY_BUTTON} w-full`}>
            Review &amp; Continue <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </button>
    ) : (
        <div className="flex gap-3">
            <button type="button" onClick={() => setStep(1)} disabled={isSubmitting} className={SECONDARY_BUTTON}>
                <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back
            </button>
            <button type="submit" form={FORM_ID} disabled={isSubmitting} data-testid="prescription-submit" className={PRIMARY_BUTTON}>
                {isSubmitting ? 'Submitting…' : 'Request reimbursement'}
            </button>
        </div>
    );

    return (
        <FormShell
            title="Request reimbursement"
            subtitle="Get your dentist's prescription approved before your treatment."
            steps={wizardSteps(STEPS, step)}
            stepsLabel="Reimbursement request steps"
            onClose={onClose}
            error={formError}
            footer={footer}
            scrollKey={step}
            testId="prescription-form"
        >
            {step === 1 && (
                <form id={FORM_ID} onSubmit={goToReview} noValidate className="space-y-4">
                    <p className="text-xs text-portal-sub">
                        Consulted a dentist outside the ToothLens network? Share their prescription for approval before your treatment.
                    </p>
                    <SelectField
                        label="Patient"
                        required
                        placeholder="Select the patient"
                        data-testid="prescription-patient"
                        id={id('patient')}
                        error={errors.patient}
                        value={patientUid}
                        onChange={(e) => { setPatientUid(e.target.value); clearError('patient'); }}
                        options={members.map((member) => ({ value: member.uid, label: memberLabel(member) }))}
                    />
                    <p className={`${SECTION_HEADING} pt-2`}>Clinic details</p>
                    <TextField label="Clinic name" required {...text('clinic_name')} />
                    <TextField label="Clinic address" required {...text('clinic_address')} />
                    <TextField label="Doctor" required {...text('clinic_doctor')} />
                    <TextField label="Clinic contact" required inputMode="tel" {...text('clinic_contact')} />

                    <p className={`${SECTION_HEADING} pt-2`}>Prescription &amp; photos</p>
                    <UploadField id={id('prescription_file')} label="Prescription" hint="On the dentist's letterhead." required testId="upload-prescription_file" error={errors.prescription_file} {...upload('prescription_file')} />
                    <TextField label="Clinical notes" required {...text('clinical_notes')} />
                    {PHOTOS.map(({ slot, label, required }) => (
                        <UploadField key={slot} id={id(slot)} label={label} required={required} testId={`upload-${slot}`} error={errors[slot]} {...upload(slot)} />
                    ))}
                </form>
            )}

            {step === 2 && (
                <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="space-y-5">
                    <section className="space-y-2">
                        <p className={SECTION_HEADING}>Patient</p>
                        <dl><ReviewField label="Patient" value={memberLabel(patient)} /></dl>
                    </section>
                    <section className="space-y-2">
                        <p className={SECTION_HEADING}>Clinic details</p>
                        <dl className="space-y-1.5">
                            <ReviewField label="Clinic name" value={fields.clinic_name} />
                            <ReviewField label="Clinic address" value={fields.clinic_address} />
                            <ReviewField label="Doctor" value={fields.clinic_doctor} />
                            <ReviewField label="Clinic contact" value={fields.clinic_contact} />
                        </dl>
                    </section>
                    <section className="space-y-2">
                        <p className={SECTION_HEADING}>Clinical notes</p>
                        <p className="text-sm text-portal-ink whitespace-pre-wrap break-words">{fields.clinical_notes}</p>
                    </section>
                    <section className="space-y-2">
                        <p className={SECTION_HEADING}>Uploaded files</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <ReviewFile label="Prescription" file={uploads.prescription_file?.file} />
                            {PHOTOS.filter(({ slot, required }) => required || uploads[slot]?.url).map(({ slot, label }) => (
                                <ReviewFile key={slot} label={label.replace(' (optional)', '')} file={uploads[slot]?.file} />
                            ))}
                        </div>
                    </section>

                    <div>
                        <label className="flex items-start gap-2.5 text-sm text-portal-ink">
                            <input
                                type="checkbox"
                                id={id('confirm')}
                                className="mt-0.5 w-4 h-4 accent-portal-purple flex-shrink-0"
                                checked={confirmed}
                                aria-invalid={confirmError ? 'true' : undefined}
                                aria-describedby={confirmError ? `${id('confirm')}-error` : undefined}
                                onChange={(e) => { setConfirmed(e.target.checked); setConfirmError(''); }}
                                data-testid="prescription-confirm"
                            />
                            <span>I confirm the information and documents provided are accurate to the best of my knowledge.</span>
                        </label>
                        {confirmError && <p id={`${id('confirm')}-error`} className="text-xs text-red-700 mt-1.5 ml-6">{confirmError}</p>}
                    </div>
                </form>
            )}
        </FormShell>
    );
};

export default PrescriptionForm;
